/* eslint-disable */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, API } from "@/lib/api";
import { Plus, FileText, Download, Trash2, Zap, ArrowRight, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATUS_STYLE = {
  draft: "bg-[#1E293B] text-[#E2E8F0]",
  sent: "bg-[#111C30] text-white",
  accepted: "bg-[#22C55E] text-white",
  refused: "bg-[#EF4444] text-white",
};

export default function OffersList() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState("");

  const statusLabel = (s) => t(`offers.status${s ? s.charAt(0).toUpperCase() + s.slice(1) : "Draft"}`, t("offers.statusDraft"));

  const load = async () => {
    try {
      const r = await api.get("/offers");
      setOffers(r.data);
    } catch (e) {
      toast.error(t("offers.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); refresh(); }, []);

  const money = (v) => (v || 0).toLocaleString("ro-RO", { maximumFractionDigits: 0 });
  const wasSent = (o) => ["sent", "accepted", "refused"].includes(o.status) || !!o.last_sent_at;
  const isOpened = (o) => !!o.last_opened_at || !!o.last_clicked_at;
  const sentCount = offers.filter(wasSent).length;
  const openedCount = offers.filter(isOpened).length;
  const acceptedCount = offers.filter((o) => o.status === "accepted").length;
  const refusedCount = offers.filter((o) => o.status === "refused").length;
  const pendingOffers = offers.filter((o) => o.status === "sent");
  const conversion = sentCount > 0 ? Math.round((acceptedCount / sentCount) * 100) : 0;
  const pipelineValue = pendingOffers.reduce((a, o) => a + (o.totals?.total || 0), 0);
  const acceptedValue = offers.filter((o) => o.status === "accepted").reduce((a, o) => a + (o.totals?.total || 0), 0);
  const isStale = (o) => o.status === "sent" && !isOpened(o) && o.last_sent_at &&
    (Date.now() - new Date(o.last_sent_at).getTime()) > 48 * 3600 * 1000;
  const staleCount = offers.filter(isStale).length;

  const sendFollowup = async (id) => {
    setFollowBusy(id);
    try {
      await api.post(`/offers/${id}/send-followup`);
      toast.success(t("offers.followupSent"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("offers.followupError"));
    } finally {
      setFollowBusy("");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("offers.confirmDelete"))) return;
    try {
      await api.delete(`/offers/${id}`);
      toast.success(t("offers.deleted"));
      load();
    } catch (e) {
      toast.error(t("common.error"));
    }
  };

  const downloadPdf = async (id, offerNumber) => {
    try {
      const res = await api.get(`/offers/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${offerNumber || "oferta"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(t("offers.pdfError"));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">{t("offers.overline")}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{t("offers.title")}</h1>
          <p className="text-sm text-[#94A3B8] mt-2" data-testid="offers-quota">
            {t("offers.quotaOffers", { count: user?.offers_this_month || 0 })} · {t("offers.plan")}: <b>{user?.plan}</b>
          </p>
        </div>
        <Link to="/app/oferte/noua" className="btn-primary flex items-center gap-2" data-testid="create-offer-btn">
          <Plus size={18}/> {t("offers.newOffer")}
        </Link>
      </div>

      {/* Pipeline mini-dashboard */}
      {!loading && offers.length > 0 && (
        <div className="mt-8" data-testid="pipeline-dashboard">
          <div className="overline mb-3">{t("offers.pipelineOverline")}</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { l: t("offers.statTotal"), v: offers.length, c: "text-[#E2E8F0]", tid: "stat-total" },
              { l: t("offers.statSent"), v: sentCount, c: "text-[#38BDF8]", tid: "stat-sent" },
              { l: t("offers.statOpened"), v: openedCount, c: "text-[#00BFFF]", tid: "stat-opened" },
              { l: t("offers.statAccepted"), v: acceptedCount, c: "text-[#22C55E]", tid: "stat-accepted" },
              { l: t("offers.statRefused"), v: refusedCount, c: "text-[#EF4444]", tid: "stat-refused" },
            ].map((s) => (
              <div key={s.tid} className="card-flat p-4" data-testid={s.tid}>
                <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{s.l}</div>
                <div className={`font-mono-plex text-2xl font-semibold mt-1 ${s.c}`}>{s.v}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div className="card-flat p-5 flex items-center justify-between glow-blue" data-testid="pipeline-conversion">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{t("offers.conversionRate")}</div>
                <div className="text-xs text-[#94A3B8] mt-1">{t("offers.conversionSub")}</div>
              </div>
              <div className="font-mono-plex text-3xl font-bold text-[#00BFFF]">{conversion}%</div>
            </div>
            <div className="card-flat p-5" data-testid="pipeline-value">
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{t("offers.pipelineValue")}</div>
              <div className="font-mono-plex text-2xl font-semibold mt-1 text-[#FBBF24]">{money(pipelineValue)} <span className="text-sm text-[#94A3B8]">RON</span></div>
              <div className="text-xs mt-0.5 flex items-center gap-1.5">
                <span className="text-[#94A3B8]">{t("offers.offersPending", { count: pendingOffers.length })}</span>
                {staleCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-[#EF4444] font-semibold" data-testid="stale-count">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" /> {t("offers.staleUnread", { count: staleCount })}
                  </span>
                )}
              </div>
            </div>
            <div className="card-flat p-5" data-testid="accepted-value">
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{t("offers.acceptedValue")}</div>
              <div className="font-mono-plex text-2xl font-semibold mt-1 text-[#22C55E]">{money(acceptedValue)} <span className="text-sm text-[#94A3B8]">RON</span></div>
              <div className="text-xs text-[#94A3B8] mt-0.5">{t("offers.offersWon", { count: acceptedCount })}</div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade banner for free plan */}
      {user && user.plan === "free" && (
        <Link
          to="/app/abonament"
          data-testid="upgrade-banner"
          className="mt-6 block bg-gradient-to-r from-[#00BFFF] to-[#FBBF24] text-white p-5 flex items-center justify-between gap-4 hover:opacity-95 transition-opacity"
        >
          <div className="flex items-center gap-4">
            <Zap size={28}/>
            <div>
              <div className="font-display font-extrabold text-lg">{t("offers.upgradeTitle")}</div>
              <div className="text-sm text-white/90 mt-0.5">{t("offers.upgradeDesc")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-[#0B111D] text-[#E2E8F0] px-4 py-2 font-semibold text-sm flex-shrink-0">
            {t("offers.seePlans")} <ArrowRight size={16}/>
          </div>
        </Link>
      )}

      <div className="mt-10">
        {loading ? (
          <div className="text-sm text-[#94A3B8]">{t("common.loading")}</div>
        ) : offers.length === 0 ? (
          <div className="card-flat p-12 text-center">
            <FileText size={36} className="mx-auto text-[#94A3B8]"/>
            <h3 className="font-display text-xl font-bold mt-4">{t("offers.emptyTitle")}</h3>
            <p className="text-sm text-[#94A3B8] mt-2">{t("offers.emptyDesc")}</p>
            <Link to="/app/oferte/noua" className="btn-primary mt-6 inline-flex items-center gap-2">
              <Plus size={16}/> {t("offers.createFirst")}
            </Link>
          </div>
        ) : (
          <div className="border border-[#1E293B]" data-testid="offers-table">
            <table className="w-full text-sm">
              <thead className="bg-[#111C30] text-white">
                <tr>
                  <th className="text-left p-4 font-display font-bold">{t("offers.thNumber")}</th>
                  <th className="text-left p-4 font-display font-bold">{t("offers.thClient")}</th>
                  <th className="text-left p-4 font-display font-bold">{t("offers.thProject")}</th>
                  <th className="text-left p-4 font-display font-bold">{t("offers.thStatus")}</th>
                  <th className="text-right p-4 font-display font-bold">{t("offers.thTotal")}</th>
                  <th className="text-left p-4 font-display font-bold">{t("offers.thDate")}</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => (
                  <tr key={o.id} className="border-b border-[#1E293B] hover:bg-[#0B111D]" data-testid={`offer-row-${o.id}`}>
                    <td className="p-4 font-mono-plex text-xs">{o.offer_number}</td>
                    <td className="p-4">{o.client_name}</td>
                    <td className="p-4 max-w-xs truncate">{o.project_title}</td>
                    <td className="p-4">
                      <span className="inline-flex items-center gap-2">
                        <span className={`font-mono-plex text-[11px] px-2 py-0.5 ${STATUS_STYLE[o.status] || STATUS_STYLE.draft}`} data-testid={`status-${o.id}`}>
                          {statusLabel(o.status)}
                        </span>
                        {isStale(o) && <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" title={t("offers.staleTitle")} data-testid={`stale-${o.id}`} />}
                      </span>
                    </td>
                    <td className="p-4 text-right font-mono-plex font-semibold">{o.totals.total.toFixed(2)} RON</td>
                    <td className="p-4 text-[#94A3B8] text-xs">{o.created_at?.slice(0,10)}</td>
                    <td className="p-4 flex items-center justify-end gap-1">
                      {o.status === "sent" && (
                        <button onClick={()=>sendFollowup(o.id)} disabled={followBusy===o.id}
                          className={`p-2 hover:bg-[#1E293B] disabled:opacity-50 ${isStale(o) ? "text-[#EF4444]" : "text-[#94A3B8]"}`}
                          title={o.follow_up_sent ? t("offers.followupResend") : t("offers.followupSend")}
                          data-testid={`followup-${o.id}`}>
                          {followBusy===o.id ? <Loader2 size={16} className="animate-spin"/> : <Bell size={16}/>}
                        </button>
                      )}
                      <Link to={`/app/oferte/${o.id}`} className="p-2 hover:bg-[#1E293B]" title={t("offers.view")} data-testid={`view-${o.id}`}>
                        <FileText size={16}/>
                      </Link>
                      <button onClick={()=>downloadPdf(o.id, o.offer_number)} className="p-2 hover:bg-[#1E293B]" title={t("offers.pdf")} data-testid={`pdf-${o.id}`}>
                        <Download size={16}/>
                      </button>
                      <button onClick={()=>remove(o.id)} className="p-2 hover:bg-red-100 text-[#EF4444]" title={t("offers.delete")} data-testid={`delete-${o.id}`}>
                        <Trash2 size={16}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
